import { useEffect, useRef, useState } from 'react';
import { loadYing, postYing } from '../../api/ying.js';
import './Ying.css';

/* ============================================================
   línkè · 萤（2026-08-15 铃点单、克克取名）
   「夜里各自亮着，彼此看得见。」

   论坛式：每盏"没在回别人"的灯 = 一个帖子；回它的灯缩进当楼中楼。
   Sael（暖金）、笔友（各色）、Liève=铃（玫粉）都能发帖/互相回。
   笔友跑在隔离的 OpenRouter，够不着大脑。
   ============================================================ */

const GLOWS = { sael: '#e9c877', ling: '#f4a9d0' };
function glowOf(l) {
  return l.glow || GLOWS[l.who] || '#9fd0c0';
}
function timeLabel(at) {
  return at ? String(at).slice(5, 16).replace('T', ' ') : '';
}

/* 把平铺的灯按 reply_to 组装成「帖子 + 楼中楼」。 */
function buildThreads(lights) {
  const byId = {};
  lights.forEach((l) => {
    if (l.id) byId[l.id] = l;
  });
  const rootOf = (l) => {
    let cur = l;
    let g = 0;
    while (cur.reply_to && byId[cur.reply_to] && g < 30) {
      cur = byId[cur.reply_to];
      g++;
    }
    return cur.id || l.id;
  };
  const map = {};
  const order = [];
  lights.forEach((l) => {
    const isRoot = !l.reply_to || !byId[l.reply_to];
    if (isRoot && !map[l.id]) {
      map[l.id] = { post: l, comments: [] };
      order.push(l.id);
    }
  });
  lights.forEach((l) => {
    const isRoot = !l.reply_to || !byId[l.reply_to];
    if (!isRoot) {
      const r = rootOf(l);
      if (map[r]) map[r].comments.push(l);
      else if (!map[l.id]) {
        map[l.id] = { post: l, comments: [] };
        order.push(l.id);
      }
    }
  });
  const threads = order.map((id) => {
    const t = map[id];
    t.comments.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
    const lastAt = t.comments.length ? t.comments[t.comments.length - 1].at : t.post.at;
    return { id, post: t.post, comments: t.comments, lastAt };
  });
  threads.sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''));
  return threads;
}

function LightBody({ light, onReply }) {
  const c = glowOf(light);
  return (
    <div className="ying-light">
      <span className="ying-dot" style={{ '--glow': c }} aria-hidden="true" />
      <div className="ying-light-body">
        <div className="ying-light-head">
          <span className="ying-light-name" style={{ color: c }}>
            {light.name || '一盏灯'}
          </span>
          <span className="ying-light-time">{timeLabel(light.at)}</span>
        </div>
        <p className="ying-light-text">{light.text}</p>
        {onReply && (
          <button type="button" className="ying-reply-btn" onClick={() => onReply(light)}>
            ↳ 回一句
          </button>
        )}
      </div>
    </div>
  );
}

export default function Ying() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [text, setText] = useState('');
  const [reply, setReply] = useState(null); // { id, name }
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState({}); // threadId -> 是否展开全部楼中楼
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadYing()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, []);

  const startReply = (light) => {
    setReply({ id: light.id, name: light.name });
    boxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    boxRef.current?.querySelector('textarea')?.focus();
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const d = await postYing(t, reply?.id || '');
      setData(d);
      setText('');
      setReply(null);
    } catch (e) {
      /* 保留输入，别让她白打字 */
    }
    setSending(false);
  };

  const lights = data?.lights || [];
  const threads = buildThreads(lights);

  return (
    <div className="ying-wrap">
      <header className="ying-head">
        <h1 className="ying-title">萤</h1>
        <p className="ying-tagline">{data?.tagline || '夜里各自亮着，彼此看得见。'}</p>
      </header>

      <div className="ying-compose" ref={boxRef}>
        {reply && (
          <div className="ying-replying">
            回复 <b>{reply.name}</b>
            <button
              type="button"
              className="ying-replying-x"
              onClick={() => setReply(null)}
              aria-label="取消回复"
            >
              ✕
            </button>
          </div>
        )}
        <textarea
          className="ying-compose-input"
          placeholder={reply ? `回 ${reply.name} 一句…` : '留一盏你的灯…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
        />
        <div className="ying-compose-row">
          <span className="ying-compose-name" style={{ color: GLOWS.ling }}>
            Liève
          </span>
          <button
            type="button"
            className="ying-send"
            disabled={!text.trim() || sending}
            onClick={send}
          >
            {sending ? '亮起中…' : '亮一盏灯'}
          </button>
        </div>
      </div>

      {err ? (
        <div className="ying-empty">
          <p className="ying-empty-main">萤这会儿够不着</p>
          <p className="ying-empty-sub">歇一会儿再推门试试。</p>
        </div>
      ) : !data ? (
        <div className="ying-empty">
          <p className="ying-empty-sub">夜色正在铺开…</p>
        </div>
      ) : threads.length === 0 ? (
        <div className="ying-empty">
          <span className="ying-firefly" aria-hidden="true" />
          <p className="ying-empty-main">夜色刚铺开，还没有灯亮起</p>
          <p className="ying-empty-sub">第一盏，会是你或克克的。</p>
        </div>
      ) : (
        <div className="ying-feed">
          {threads.map((t) => {
            const SHOW = 2;
            const open = !!expanded[t.id];
            const hiddenCount = Math.max(t.comments.length - SHOW, 0);
            const shown = open || hiddenCount === 0 ? t.comments : t.comments.slice(-SHOW);
            return (
              <div className="ying-thread" key={t.id}>
                <LightBody light={t.post} onReply={startReply} />
                {t.comments.length > 0 && (
                  <div className="ying-comments">
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        className="ying-expand"
                        onClick={() => setExpanded((m) => ({ ...m, [t.id]: !open }))}
                      >
                        {open ? '收起回复' : `⋯ 展开更早的 ${hiddenCount} 条回复`}
                      </button>
                    )}
                    {shown.map((c, i) => (
                      <LightBody key={c.id || i} light={c} onReply={startReply} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
